import { useMemo } from "react";
import styled from "@emotion/styled";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { Flex, Text } from "styles/utility-components";

import NavItem from "components/app_layout/vertical_nav/NavItem";
import { CONTENT_LIBRARY_MANAGEMENT, useVariableValue } from "lib/featureFlags";
import { useCustomerDocuments } from "lib/hooks/api/customer_documents/useCustomerDocuments";
import useParagonUser from "lib/hooks/api/integrations/useParagonUser";
import { IconProps } from "lib/icons/untitled_ui/UIcon";
import { paragon } from "lib/integrations/paragon";
import { contentLibraryRoutes, settingsRoutes } from "lib/routes";
import {
  CustomerDocumentReviewStatus,
  CustomerDocumentSortBy,
  CustomerDocumentSortOrder,
  CustomerDocumentTypeFilter,
} from "lib/types/graphQLEnums";

type Nav = {
  title: string;
  icon: IconProps["icon"];
  route: string;
};

type Props = {
  children: React.ReactNode;
};
export default function ContentLibraryNavigation({ children }: Props) {
  const { user } = useParagonUser();
  const isContentLibraryManagementEnabled = useVariableValue(CONTENT_LIBRARY_MANAGEMENT, false);

  const { totalCount: toReviewCount } = useCustomerDocuments(
    {
      documentType: CustomerDocumentTypeFilter.ContentLibrary,
      onlyFetchFiles: true,
      reviewStatus: [CustomerDocumentReviewStatus.Warning, CustomerDocumentReviewStatus.Overdue],
      sortBy: CustomerDocumentSortBy.CreatedAt,
      sortOrder: CustomerDocumentSortOrder.Desc,
    },
    { page: 1, pageSize: 1 },
    { enabled: isContentLibraryManagementEnabled },
  );

  const { totalCount: unassignedCount } = useCustomerDocuments(
    {
      documentType: CustomerDocumentTypeFilter.ContentLibrary,
      onlyFetchFiles: true,
      owner: { ids: [], includeNulls: true },
      sortBy: CustomerDocumentSortBy.CreatedAt,
      sortOrder: CustomerDocumentSortOrder.Desc,
    },
    { page: 1, pageSize: 1 },
    { enabled: isContentLibraryManagementEnabled },
  );

  const navItems: Nav[] = useMemo(() => {
    const navItems: Nav[] = [
      {
        title: "Uploads",
        icon: "folder",
        route: contentLibraryRoutes.uploads,
      },
    ];
    const intMetadata = paragon.getIntegrationMetadata();
    const cmsProviders = ["googledrive", "sharepoint"];
    for (const provider of cmsProviders) {
      const integration = intMetadata.find((int) => int.type === provider);
      if (integration && user?.integrations?.[provider]?.enabled) {
        navItems.push({
          title: integration.name,
          icon: provider as IconProps["icon"],
          route: contentLibraryRoutes[provider as keyof typeof contentLibraryRoutes],
        });
      }
    }
    return navItems;
  }, [user]);

  const navigate = useNavigate();
  const { pathname } = useLocation();

  const isToReviewActive = pathname.includes(contentLibraryRoutes.toReview);
  const isUnassignedActive = pathname.includes(contentLibraryRoutes.unassigned);

  return (
    <Flex width="100%">
      <NavContainer>
        <Text h1>Library</Text>
        {isContentLibraryManagementEnabled && (
          <Flex column gap={8}>
            <NavItem
              title="To review"
              icon="alertTriangle"
              isActive={isToReviewActive}
              unreadNotifications={toReviewCount || undefined}
              onClick={() => navigate({ to: contentLibraryRoutes.toReview })}
            />
            <NavItem
              title="Unassigned"
              icon="userPlus01"
              isActive={isUnassignedActive}
              unreadNotifications={unassignedCount || undefined}
              onClick={() => navigate({ to: contentLibraryRoutes.unassigned })}
            />
          </Flex>
        )}
        <Flex column gap={8}>
          {navItems.map(({ title, icon, route }) => (
            <NavItem
              key={title}
              title={title}
              icon={icon}
              isActive={pathname.includes(route)}
              onClick={() => navigate({ to: route })}
            />
          ))}
        </Flex>
        <NavItem title="Integrations" icon="plus" path={settingsRoutes.integrations} />
      </NavContainer>
      {children}
    </Flex>
  );
}

const NavContainer = styled(Flex)(({ theme }) => ({
  flexDirection: "column",
  gap: 24,
  padding: 24,
  height: "100vh",
  width: "240px",
  borderRight: `1px solid ${theme.colors.sysBorderDecorative}`,
  flexShrink: 0,
}));
